import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PlexusBackground from "../components/PlexusBackground";
import CookieDisclaimer from "../components/CookieDisclaimer";
import SEO from "../components/SEO";
import videoData from '../data/videos.json';
import "../styles.css";

// Latest game reviews data (first 3 from Games page)
const latestReviews = [
  {
    id: 116,
    title: "Uncharted 4: A Thief's End (Remastered)",
    poster: "https://images.igdb.com/igdb/image/upload/t_cover_big/co4eiw.png",
    rating: 8,
    review: ["Uncharted 4: A Thief's End is a stunning finale for Nathan Drake, with an excellent story, fantastic performances, and top-tier presentation—from gorgeous, richly detailed environments and ruins to a memorable score and even the charming Crash Bandicoot minigame, all boosted further by 120fps support in the Legacy of Thieves remaster."],
    releaseYear: 2022,
    platform: "PS5 Pro",
  },
  {
    id: 115,
    title: "Watch Dogs 2",
    poster: "https://images.igdb.com/igdb/image/upload/t_cover_big/co2p3j.png",
    rating: 8,
    review: ["Watch Dogs 2 delivers a much more vibrant and playful experience than the first game, with excellent parkour, a fun, quirky lead in Marcus, and a likable DedSec crew (Sitara, Horatio, Wrench) that many reviewers highlight as a big step up from Aiden and Chicago's gloomier tone"],
    releaseYear: 2016,
    platform: "PS4",
  },
  {
    id: 114,
    title: "Control",
    poster: "https://images.igdb.com/igdb/image/upload/t_cover_big/co2evj.webp",
    rating: 6,
    review: ["Control just wasn't the game for me, even though I can see why some people love it. The atmosphere and visual style are striking, but the story often feels deliberately vague and hard to follow."],
    releaseYear: 2019,
    platform: "PS5 Pro",
  },
];

const getRatingClass = (rating) => {
  if (rating === 10) return 'rating-legendary';
  if (rating >= 7) return 'rating-high';
  if (rating >= 4) return 'rating-mid';
  if (rating >= 1) return 'rating-low';
  return 'rating-default';
};

export default function Home() {
  // State for YouTube videos
  const [videos] = useState(videoData);
  const [loading] = useState(false);
  const [error] = useState(null);

  // State for featured products
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch('/products.json');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const allProducts = await response.json();

        // Map all products to the required format
        const formattedProducts = allProducts.map(p => ({
          id: p.id,
          img: p.overlayImage,
          alt: p.name,
          tag: (p.cost || 'free').toLowerCase(),
          title: p.name,
          description: p.description,
          link: p.buttonLink,
          btnClass: p.cost === 'Premium' ? 'btn btn-product-premium2' : 'btn btn-product',
          btnText: p.buttonText, // Use dynamic button text
          featured: p.featured,
        }));

        // Filter for featured products, or fallback to the first 3 if none are featured
        const featured = formattedProducts.filter(p => p.featured);
        const featuredReversed = featured.slice().reverse();
        setFeaturedProducts(featuredReversed.length > 0 ? featuredReversed.slice(0, 3) : formattedProducts.slice(0, 3));

      } catch (e) {
        console.error("Failed to fetch products for home page:", e);
        setProductsError("Failed to load featured tools. Please ensure 'npm run fetch-products' was successful and try again.");
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, []);


  return (
    <>
      <SEO
        title="Home"
        description="Welcome to ZeMaxi's digital hub! Watch live Twitch streams, explore free streaming tools for OBS Studio and Streamer.bot, and discover content that will elevate your streaming setup."
        keywords="ZeMaxi, Twitch streamer, streaming tools, OBS overlays, Streamer.bot, free stream tools, gaming streamer, live stream, content creator"
        url="https://zemaxi.com/"
      />
      <Navbar />
      <CookieDisclaimer />
      <main>
        <section className="hero" style={{position: 'relative', overflow: 'hidden'}}>
          <div className="container">
            <div className="hero-content">
              <h1>ZEMAXI: <span className="highlight">Streamer & Tool Creator</span></h1>
              <p>Welcome to my digital hub for streaming entertainment and powerful tools for content creators. Explore my Twitch streams, YouTube content, and discover tools that will take your streaming setup to the next level.</p>
              <div className="hero-buttons">
                <a href="https://twitch.tv/zemaxi" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-twitch"><i className="fab fa-twitch"></i> Watch on Twitch</a>
                <a href="https://youtube.com/@zemaxi/live" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-youtube"><i className="fab fa-youtube"></i> Watch on YouTube</a>
                <a href="https://kick.com/zemaxi" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-kick"><svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" className="kick-icon-hero"><path d="M37 .036h164.448v113.621h54.71v-56.82h54.731V.036h164.448v170.777h-54.73v56.82h-54.711v56.8h54.71v56.82h54.73V512.03H310.89v-56.82h-54.73v-56.8h-54.711v113.62H37V.036z" fill="currentColor"/></svg> Watch on Kick</a>
                <Link to="/shop" className="btn btn-secondary">
                  <i className="fas fa-wrench"></i> Browse Stream Tools
                </Link>
              </div>
            </div>
          </div>
          <div className="hero-background">
            <div className="grid"></div>
            <div className="glowing-lines"></div>
            {/* Plexus animated background */}
            <PlexusBackground />
          </div>
        </section>

        <section className="featured-products">
          <div className="container">
            <h2>Featured <span className="highlight">Tools</span></h2>
            <div className="product-grid">
              {productsLoading ? (
                <div className="shop-loading">
                  <div className="spinner"></div>
                  <p>Loading tools...</p>
                </div>
              ) : productsError ? (
                <div className="shop-error">
                  <p>{productsError}</p>
                </div>
              ) : (
                featuredProducts.map((product, idx) => (
                  <div className="product-card" key={product.id || idx}>
                    <div className="product-image">
                      <img src={product.img} alt={product.alt} />
                    </div>
                    <div className="product-info">
                      <div className={`product-tag ${product.tag}`}>{product.tag === "premium" ? "Premium" : product.tag.charAt(0).toUpperCase() + product.tag.slice(1)}</div>
                      <h3>{product.title}</h3>
                      <p>{product.description}</p>
                      {product.link.startsWith('/') ? (
                                        <Link to={product.link} className={product.btnClass}>{product.btnText}</Link>
                                      ) : (
                                        <a href={product.link} className={product.btnClass} target="_blank" rel="noopener noreferrer">{product.btnText}</a>
                                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="section-cta">
              <Link to="/shop" className="btn btn-secondary">Browse All Tools</Link>
            </div>
          </div>
        </section>

        <section className="latest-videos">
          <div className="container">
            <h2>Latest <span className="highlight">Videos</span></h2>
            <div className="video-grid" id="youtube-videos">
              {loading && (
                <div className="video-loading">
                  <div className="spinner"></div>
                  <p>Loading latest videos...</p>
                </div>
              )}
              {error && (
                <div className="video-loading">
                  <p style={{ color: 'red' }}>Failed to load videos: {error}</p>
                </div>
              )}
              {!loading && !error && videos.length === 0 && (
                <div className="video-loading">
                  <p>No videos found.</p>
                </div>
              )}
              {/* Show only the first 3 manual videos */}
              {!loading && !error && videos.slice(0, 3).map(video => (
                <div className="video-item" key={video.id.videoId}>
                  <a href={`https://www.youtube.com/watch?v=${video.id.videoId}`} target="_blank" rel="noopener noreferrer">
                    <div className="video-thumbnail">
                      <img src={video.snippet.thumbnails.high.url} alt={video.snippet.title} />
                    </div>
                    <div className="video-info">
                      <h3>{video.snippet.title}</h3>
                      <div className="video-meta">
                        <span>{new Date(video.snippet.publishedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </a>
                </div>
              ))}
            </div>
            <div className="section-cta">
              <Link to="/videos" className="btn btn-secondary">View All Videos</Link>
            </div>
          </div>
        </section>

        <section className="latest-reviews">
          <div className="container">
            <h2>Latest <span className="highlight">Reviews</span></h2>
            <div className="reviews-grid">
              {latestReviews.map((game) => (
                <div className="review-card" key={game.id}>
                  <div className="review-poster">
                    <img src={game.poster} alt={game.title} />
                    <div className={`review-rating ${getRatingClass(game.rating)}`}>
                      <span className={`rating-value ${getRatingClass(game.rating)}`}>{game.rating}</span>
                      <span className="rating-max">/10</span>
                    </div>
                  </div>
                  <div className="review-info">
                    <h3>{game.title}</h3>
                    <div className="review-meta">
                      <span className="review-year">{game.releaseYear}</span>
                      <span className="review-platform">{game.platform}</span>
                    </div>
                    <p className="review-excerpt">
                      {Array.isArray(game.review) ? game.review[0].substring(0, 150) : game.review.substring(0, 150)}...
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="section-cta">
              <Link to="/games" className="btn btn-secondary">View All Reviews</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
